import React, { useState, useEffect, useRef, useMemo } from 'react';
import Modal from '../../../BaseComponents/Modal';
import TextInput from '../../../BaseComponents/TextInput';
import Select, { SelectOption } from '../../../BaseComponents/Select';
import Button from '../../../BaseComponents/Button';
import { NoteTemplate, createNoteTemplate, updateNoteTemplate, getNoteTemplateById } from '../../../../api/note-templates';
import { toast } from 'react-toastify';
import { NOTE_TEMPLATE_CATEGORIES } from '../../../../utils/constants';
import './NoteTemplateModal.scss';
interface NoteTemplatesModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaved: (data: any) => void;
    templateToEdit?: NoteTemplate | null;
}

const CATEGORY_OPTIONS: SelectOption[] = NOTE_TEMPLATE_CATEGORIES;


const CustomTrixEditor: React.FC<{
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
    className?: string;
}> = ({ value, onChange, placeholder, className }) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const onChangeRef = useRef(onChange);
    const valueRef = useRef(value);
    onChangeRef.current = onChange;
    valueRef.current = value;
    const inputId = useMemo(() => `trix-input-${Math.random().toString(36).substr(2, 9)}`, []);

    useEffect(() => {
        const input = inputRef.current;
        const editor = editorRef.current;
        if (!input || !editor) return;

        const trixEditor = document.createElement('trix-editor');
        trixEditor.setAttribute('input', inputId);
        if (placeholder) trixEditor.setAttribute('placeholder', placeholder);

        editor.innerHTML = '';
        editor.appendChild(trixEditor);

        input.value = valueRef.current || '';
        (trixEditor as any).editor.loadHTML(valueRef.current || '');

        const handleChange = () => {
            onChangeRef.current(trixEditor.innerHTML);
        };

        trixEditor.addEventListener('trix-change', handleChange);

        return () => {
            trixEditor.removeEventListener('trix-change', handleChange);
        };
    }, [inputId, placeholder]);

    return (
        <div className={className}>
            <input ref={inputRef} id={inputId} type="hidden" value={value} />
            <div ref={editorRef} />
        </div>
    );
};

const NoteTemplatesModal: React.FC<NoteTemplatesModalProps> = ({
    isOpen,
    onClose,
    onSaved,
    templateToEdit
}) => {
    const [formData, setFormData] = useState({
        name: '',
        category: null as SelectOption | null,
        template: ''
    });
    const [errors, setErrors] = useState({
        name: false,
        category: false,
        template: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);


    useEffect(() => {
        if (isOpen) {
            if (templateToEdit) {
                setIsFetching(true);
                getNoteTemplateById(templateToEdit.id)
                    .then((fullTemplate) => {
                        const category = CATEGORY_OPTIONS.find(opt => opt.key === fullTemplate.category) || null;
                        setFormData({
                            name: fullTemplate.name,
                            category: category,
                            template: fullTemplate.template
                        });
                    })
                    .catch((err) => {
                        console.error('Failed to fetch template details:', err);
                        setServerError('Failed to load template details');
                    })
                    .finally(() => {
                        setIsFetching(false);
                    });
            } else {
                setFormData({
                    name: '',
                    category: null,
                    template: ''
                });
                setErrors({
                    name: false,
                    category: false,
                    template: false
                })
            }
        }
    }, [isOpen, templateToEdit]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (name === 'name' && value.trim()) {
            setErrors(prev => ({ ...prev, name: false }));
        }
    };

    const handleSelectChange = (option: SelectOption) => {
        setFormData(prev => ({
            ...prev,
            category: option
        }));
        setErrors(prev => ({ ...prev, category: false }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const isTemplateEmpty = !formData.template.trim() || formData.template === '<div><br></div>' || formData.template === '';

        const newErrors = {
            name: !formData.name.trim(),
            category: !formData.category,
            template: isTemplateEmpty
        };

        setErrors(newErrors);

        if (newErrors.name || newErrors.category || newErrors.template) {
            return;
        }

        setIsLoading(true);
        setServerError(null);

        const templateData = {
            name: formData.name,
            category: formData.category?.key || '',
            template: formData.template
        };

        const apiCall = templateToEdit
            ? updateNoteTemplate({ ...templateData, id: templateToEdit.id })
            : createNoteTemplate(templateData);

        apiCall
            .then((res) => {
                toast.success(`Note template ${templateToEdit ? 'updated' : 'added'} successfully`);
                onSaved(res);
            })
            .catch((err) => {
                setServerError(err.message || 'An error occurred while saving the template');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={templateToEdit ? 'Edit Note Template' : 'Add Note Template'}
            width="900px"
        >
            {isFetching ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>Loading template details...</div>
            ) : (
                <form onSubmit={handleSubmit} className="note-template-form-modal">
                    <div className="form-group">
                        <TextInput
                            name="name"
                            title="Name of Template*"
                            placeholder="Enter template name"
                            type="text"
                            value={formData.name}
                            onChange={handleInputChange}
                            isError={errors.name}
                            errorMessage={errors.name ? 'Name is required' : undefined}
                        />
                    </div>

                    <div className="form-group" style={{ zIndex: 100 }}>
                        <Select
                            name="category"
                            title="Category *"
                            options={CATEGORY_OPTIONS}
                            selectedOption={formData.category}
                            onChange={handleSelectChange}
                            placeholder="Select category"
                            isError={errors.category}
                            errorMessage={errors.category ? 'Category is required' : undefined}
                        />
                    </div>

                    <div className="form-group">
                        <div className="section-title" style={{ marginBottom: '8px', fontWeight: '500' }}>Template *</div>
                        <div className="trix-editor-wrapper">
                            <CustomTrixEditor
                                value={formData.template}
                                onChange={(html) => {
                                    setFormData(prev => ({ ...prev, template: html }));
                                    if (html.trim() && html !== '<div><br></div>') {
                                        setErrors(prev => ({ ...prev, template: false }));
                                    }
                                }}
                                placeholder="Enter template text..."
                                className="trix-editor"
                            />
                        </div>
                        {errors.template && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>Template is required</div>}
                    </div>

                    <div className="form-actions">
                        {serverError && <div style={{ color: 'red', marginBottom: '10px' }}>{serverError}</div>}
                        <Button
                            type="submit"
                            text={templateToEdit ? 'Update Note Template' : 'Add Note Template'}
                            isDisabled={!formData.name.trim() || !formData.category || !formData.template.trim() || isLoading}
                        />
                    </div>
                </form>
            )}
        </Modal>
    );
};

export default NoteTemplatesModal;
